import {tx, ptx, usePageText} from '../../lib/page-text';
import { bounds, type Shape } from "./model";
let measure: CanvasRenderingContext2D | null | undefined;
function wrap(text: string, width: number, font: string) {
  if (measure === undefined)
    measure = document.createElement("canvas").getContext("2d");
  if (measure) measure.font = font;
  const fits = (s: string) =>
    (measure?.measureText(s).width ?? s.length * 14) <= width;
  const lines: string[] = [];
  for (const paragraph of text.split("\n")) {
    let line = "";
    for (const word of paragraph.split(" ")) {
      const trial = line ? line + " " + word : word;
      if (fits(trial)) {
        line = trial;
        continue;
      }
      if (line) lines.push(line);
      line = "";
      for (const char of word) {
        if (line && !fits(line + char)) {
          lines.push(line);
          line = "";
        }
        line += char;
      }
    }
    lines.push(line);
  }
  return lines;
}
export function BoardShapeView({
  shape: s,
  image,
  prefix,
}: {
  shape: Shape;
  image?: string;
  prefix: string;
}) {
  usePageText();
  const b = bounds(s),
    [a, z = a] = s.points,
    fill = s.fill ?? (s.kind === "note" ? "#fef3c7" : "none"),
    stroke = {
      stroke: s.color,
      strokeWidth: s.width,
      fill,
      strokeLinejoin: "round" as const,
    },
    size = s.fontSize ?? 24;
  let element;
  if (s.kind === "image")
    element = image ? (
      <image
        href={image}
        x={b.x}
        y={b.y}
        width={b.w}
        height={b.h}
        preserveAspectRatio="xMidYMid meet"
      />
    ) : (
      <g>
        <rect
          x={b.x}
          y={b.y}
          width={b.w}
          height={b.h}
          fill="#f1f5f9"
          stroke="#94a3b8"
        />
        <text x={b.x + 8} y={b.y + 24} fontSize="16" fill="#475569">{tx("Loading image…")}</text>
      </g>
    );
  else if (s.kind === "text" || s.kind === "note") {
    if (s.points.length === 1)
      element = (
        <text
          x={a[0]}
          y={a[1]}
          fill={s.color}
          fontSize={size}
          fontFamily={s.font ?? "sans-serif"}
        >
          {s.text}
        </text>
      );
    else {
      const clip = `${prefix}-${encodeURIComponent(s.id)}`,
        font = `${s.italic ? "italic " : ""}${s.bold ? "bold " : ""}${size}px ${s.font ?? "sans-serif"}`,
        lines = wrap(s.text ?? "", Math.max(10, b.w - 16), font),
        align = s.align ?? "left";
      element = (
        <g>
          <defs>
            <clipPath id={clip}>
              <rect x={b.x} y={b.y} width={b.w} height={b.h} />
            </clipPath>
          </defs>
          <rect
            x={b.x}
            y={b.y}
            width={b.w}
            height={b.h}
            fill={fill}
            rx={s.kind === "note" ? 4 : 0}
          />
          <text
            clipPath={`url(#${clip})`}
            fill={s.color}
            fontSize={size}
            fontFamily={s.font ?? "sans-serif"}
            fontWeight={s.bold ? "bold" : "normal"}
            fontStyle={s.italic ? "italic" : "normal"}
            textAnchor={
              align === "center"
                ? "middle"
                : align === "right"
                  ? "end"
                  : "start"
            }
          >
            {lines.map((line, i) => (
              <tspan
                key={i}
                x={
                  align === "center"
                    ? b.x + b.w / 2
                    : align === "right"
                      ? b.x + b.w - 8
                      : b.x + 8
                }
                y={b.y + 8 + size + i * size * 1.25}
              >
                {line || " "}
              </tspan>
            ))}
          </text>
        </g>
      );
    }
  } else if (s.kind === "rect")
    element = <rect x={b.x} y={b.y} width={b.w} height={b.h} {...stroke} />;
  else if (s.kind === "ellipse")
    element = (
      <ellipse
        cx={b.x + b.w / 2}
        cy={b.y + b.h / 2}
        rx={b.w / 2}
        ry={b.h / 2}
        {...stroke}
      />
    );
  else if (s.kind === "triangle" || s.kind === "diamond") {
    const pts =
      s.kind === "triangle"
        ? [
            [b.x + b.w / 2, b.y],
            [b.x + b.w, b.y + b.h],
            [b.x, b.y + b.h],
          ]
        : [
            [b.x + b.w / 2, b.y],
            [b.x + b.w, b.y + b.h / 2],
            [b.x + b.w / 2, b.y + b.h],
            [b.x, b.y + b.h / 2],
          ];
    element = (
      <polygon points={pts.map((p) => p.join(",")).join(" ")} {...stroke} />
    );
  } else if (s.kind === "line" || s.kind === "arrow") {
    const angle = Math.atan2(z[1] - a[1], z[0] - a[0]),
      len = Math.max(12, s.width * 4);
    element = (
      <g>
        <line
          x1={a[0]}
          y1={a[1]}
          x2={z[0]}
          y2={z[1]}
          stroke={s.color}
          strokeWidth={s.width}
          strokeLinecap="round"
        />
        {s.kind === "arrow" && (
          <polygon
            points={[
              z,
              [
                z[0] - len * Math.cos(angle - 0.45),
                z[1] - len * Math.sin(angle - 0.45),
              ],
              [
                z[0] - len * Math.cos(angle + 0.45),
                z[1] - len * Math.sin(angle + 0.45),
              ],
            ]
              .map((p) => p.join(","))
              .join(" ")}
            fill={s.color}
          />
        )}
      </g>
    );
  } else
    element = (
      <polyline
        points={(s.points.length === 1 ? [a, [a[0] + 0.1, a[1]]] : s.points)
          .map((p) => p.join(","))
          .join(" ")}
        fill="none"
        stroke={s.color}
        strokeWidth={s.width}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    );
  return (
    <g data-shape={s.id} opacity={s.opacity ?? 1}>
      {!["pen", "line", "arrow"].includes(s.kind) && (
        <rect
          data-editor="hit-area"
          x={b.x}
          y={b.y}
          width={b.w}
          height={b.h}
          fill="transparent"
        />
      )}
      {element}
    </g>
  );
}
